import * as React from "react";

import { IntlProvider as ReactIntlProvider } from "react-intl";

import { getLocaleMessages } from "@/i18n/i18n.utils";

import {
  Locale,
  LocaleData,
  LocaleDataMap,
  LocaleUrlPrefix,
} from "./i18n.types";

export type IntlProviderProps = {
  locale: Locale;
  localeDataMap: LocaleDataMap;
  children: React.ReactNode;
};

type IntlProviderContextValue = Omit<IntlProviderProps, "children"> & {
  urlPrefix: LocaleUrlPrefix;
  localeData: LocaleData;
};

const IntlContext = React.createContext<IntlProviderContextValue | undefined>(
  undefined,
);

export const IntlProvider = ({
  children,
  locale,
  localeDataMap,
}: IntlProviderProps) => {
  const messages = getLocaleMessages(locale);

  const [urlPrefix, setUrlPrefix] = React.useState<LocaleUrlPrefix>(
    localeDataMap[locale].url_prefix,
  );
  const [localeData, setLocaleData] = React.useState<LocaleData>(
    localeDataMap[locale],
  );

  React.useEffect(() => {
    setUrlPrefix(localeDataMap[locale].url_prefix);
    setLocaleData(localeDataMap[locale]);
  }, [locale, localeDataMap]);

  return (
    <ReactIntlProvider locale={locale || "en-US"} messages={messages}>
      <IntlContext.Provider
        value={{ locale, localeData, localeDataMap, urlPrefix }}
      >
        {children}
      </IntlContext.Provider>
    </ReactIntlProvider>
  );
};

export const useIntlContext = () => {
  const context = React.useContext(IntlContext);
  if (!context) {
    throw new Error("useIntlContext must be used within an IntlProvider");
  }
  return context;
};
